/**
 * Phase 27.45 — Lead Source picker.
 *
 * Renders a Source dropdown and, when the user picks `event`, an
 * additional Event Request dropdown that lists every approved event
 * the caller is authorised to attribute leads to.
 *
 * Emits changes to the parent via `onChange({ source, event_request_id })`
 * so the parent form state stays flat.
 */
import { useEffect, useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, CalendarClock, Store } from 'lucide-react';
import { eventRequestsAPI, retailProgramAPI } from '@/lib/api';

export const SOURCE_OPTIONS = [
  { value: 'manual', label: 'Manual entry' },
  { value: 'self_added', label: 'Self added' },
  { value: 'event', label: 'Event' },
  { value: 'kiosk_event', label: 'Live Event Kiosk' },
  { value: 'retail_program', label: 'Retail Program' },
  { value: 'referral', label: 'Referral' },
  { value: 'website', label: 'Website' },
  { value: 'landing_page', label: 'Landing page' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'import', label: 'Import' },
  { value: 'other', label: 'Other' },
];

function labelForSource(value) {
  const known = SOURCE_OPTIONS.find((o) => o.value === value);
  if (known) return known.label;
  if (!value) return null;
  // Legacy free-text values (e.g. from CSV imports) are kept verbatim.
  return value;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

export default function LeadSourcePicker({
  source,
  eventRequestId,
  retailAssignmentId,
  onChange,
  testId = 'lead-source-picker',
  compact = false,
}) {
  const [events, setEvents] = useState([]);
  const [retailAssignments, setRetailAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingRetail, setLoadingRetail] = useState(false);

  const isEvent = source === 'event';
  const isRetail = source === 'retail_program';

  useEffect(() => {
    if (!isEvent) return;
    let cancelled = false;
    setLoading(true);
    eventRequestsAPI.leadOptions()
      .then((r) => { if (!cancelled) setEvents(r.data?.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isEvent]);

  useEffect(() => {
    if (!isRetail) return;
    let cancelled = false;
    setLoadingRetail(true);
    retailProgramAPI.leadOptions()
      .then((r) => { if (!cancelled) setRetailAssignments(r.data?.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingRetail(false); });
    return () => { cancelled = true; };
  }, [isRetail]);

  const options = useMemo(() => {
    // If the current `source` value is a legacy free-text string that
    // isn't in SOURCE_OPTIONS, surface it as an extra item so the
    // Select doesn't render blank while editing.
    if (source && !SOURCE_OPTIONS.find((o) => o.value === source)) {
      return [{ value: source, label: labelForSource(source) }, ...SOURCE_OPTIONS];
    }
    return SOURCE_OPTIONS;
  }, [source]);

  const handleSource = (value) => {
    if (value === 'event') {
      onChange({ source: value, event_request_id: eventRequestId || null, retail_assignment_id: null });
    } else if (value === 'retail_program') {
      onChange({ source: value, event_request_id: null, retail_assignment_id: retailAssignmentId || null });
    } else {
      onChange({ source: value, event_request_id: null, retail_assignment_id: null });
    }
  };

  const handleEvent = (value) => {
    onChange({ source: 'event', event_request_id: value, retail_assignment_id: null });
  };

  const handleRetail = (value) => {
    onChange({ source: 'retail_program', event_request_id: null, retail_assignment_id: value });
  };

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      <div>
        <Label className="text-xs">Source</Label>
        <Select value={source || ''} onValueChange={handleSource}>
          <SelectTrigger data-testid={`${testId}-source`}>
            <SelectValue placeholder="Pick a source" />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isEvent && (
        <div>
          <Label className="text-xs flex items-center gap-1">
            <CalendarClock size={12} className="text-purple-500" />
            Event
          </Label>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 h-9">
              <Loader2 size={12} className="animate-spin" /> Loading events…
            </div>
          ) : events.length === 0 ? (
            <p className="text-xs text-slate-500 italic py-2">
              No approved events available yet. Ask an admin to approve one in Marketing Center.
            </p>
          ) : (
            <Select
              value={eventRequestId || ''}
              onValueChange={handleEvent}
            >
              <SelectTrigger data-testid={`${testId}-event`}>
                <SelectValue placeholder="Pick an event" />
              </SelectTrigger>
              <SelectContent>
                {events.map((ev) => (
                  <SelectItem key={ev.request_id} value={ev.request_id}>
                    <span className="font-medium">{ev.event_name}</span>
                    <span className="text-slate-500 ml-2 text-xs">
                      {fmtDate(ev.event_date)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {isRetail && (
        <div>
          <Label className="text-xs flex items-center gap-1">
            <Store size={12} className="text-emerald-600" />
            Retail Assignment
          </Label>
          {loadingRetail ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 h-9">
              <Loader2 size={12} className="animate-spin" /> Loading assignments…
            </div>
          ) : retailAssignments.length === 0 ? (
            <p className="text-xs text-slate-500 italic py-2">
              No approved retail assignments yet. Ask a KR admin to approve one in
              Marketing Center → Retail Program.
            </p>
          ) : (
            <Select
              value={retailAssignmentId || ''}
              onValueChange={handleRetail}
            >
              <SelectTrigger data-testid={`${testId}-retail`}>
                <SelectValue placeholder="Pick an assignment" />
              </SelectTrigger>
              <SelectContent>
                {retailAssignments.map((ra) => (
                  <SelectItem key={ra.retail_assignment_id} value={ra.retail_assignment_id}>
                    {ra.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
    </div>
  );
}

export { labelForSource };
